/**
 * O que aparece enquanto a marca ainda não é conhecida.
 *
 * Só na primeira visita a uma porta: sem cache e sem resposta da API, não há
 * cor para mostrar — e mostrar a Eva para depois trocar era exatamente a
 * piscada. Este esqueleto é neutro de propósito: cinzas, sem `--eva-brand`,
 * sem logo. No telefone tem a forma da tela de entrar (logo, dois campos, um
 * botão); acima, a forma de um portal (barra lateral e área de conteúdo).
 * Não usa Mantine: precisa existir antes do tema, que é da marca.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { PHONE_MEDIA_QUERY } from '../tokens';

const CINZA = '#E5E7EB';
const FUNDO = '#F9FAFB';

const bloco = (w: string | number, h: number, radius = 8): CSSProperties => ({
  width: w,
  height: h,
  borderRadius: radius,
  background: `linear-gradient(90deg, ${CINZA} 25%, #F3F4F6 37%, ${CINZA} 63%)`,
  backgroundSize: '400% 100%',
  animation: 'eva-esqueleto 1.4s ease infinite',
});

const keyframes = `@keyframes eva-esqueleto { 0% { background-position: 100% 50% } 100% { background-position: 0 50% } }
@media (prefers-reduced-motion: reduce) { .eva-esqueleto * { animation: none !important } }`;

export function CascaEsqueleto() {
  const [telefone, setTelefone] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(PHONE_MEDIA_QUERY).matches : true));
  useEffect(() => {
    const mq = window.matchMedia(PHONE_MEDIA_QUERY);
    const ouvir = () => setTelefone(mq.matches);
    mq.addEventListener('change', ouvir);
    return () => mq.removeEventListener('change', ouvir);
  }, []);

  return (
    <div className="eva-esqueleto" aria-busy="true" aria-label="Carregando" style={{ minHeight: '100dvh', background: FUNDO, color: 'transparent' }}>
      <style>{keyframes}</style>
      {telefone ? (
        <div style={{ maxWidth: 360, margin: '0 auto', padding: '64px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div style={bloco(56, 56, 12)} />
          <div style={{ ...bloco(180, 20), marginTop: 8 }} />
          <div style={bloco(220, 12)} />
          <div style={{ ...bloco('100%', 44), marginTop: 24 }} />
          <div style={bloco('100%', 44)} />
          <div style={{ ...bloco('100%', 44), marginTop: 8 }} />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', minHeight: '100dvh' }}>
          <div style={{ padding: 24, borderRight: `1px solid ${CINZA}`, background: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ ...bloco(48, 48, 12), marginBottom: 20 }} />
            {[0, 1, 2, 3, 4].map((i) => <div key={i} style={bloco('80%', 16)} />)}
          </div>
          <div style={{ padding: 40, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={bloco(280, 28)} />
            <div style={{ ...bloco(420, 14), marginBottom: 16 }} />
            {[0, 1, 2].map((i) => <div key={i} style={bloco('100%', 64, 12)} />)}
          </div>
        </div>
      )}
    </div>
  );
}
